// F11 Phase A · Dashboard "Current plan + usage" widget
// Mounted inside DashboardV3. Shows current plan + 3 usage bars.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Users, Sparkles, TrendingUp } from "lucide-react";

import { apiFetch } from "@/lib/api";

const COLOR = {
    good: "#CCCCCC",
    warn: "#A3A3A3",
    bad: "#FAFAFA",
    muted: "#1A1A1A",
};

function toneForPct(pct) {
    if (pct == null) return "muted";
    if (pct >= 50) return "good";
    if (pct >= 20) return "warn";
    return "bad";
}

function Bar({ used, limit, label, testId }) {
    const unlimited = limit === -1;
    const pct = unlimited ? 100 : limit > 0 ? Math.min(100, Math.round(100 * used / limit)) : 0;
    const remainingPct = unlimited ? 100 : 100 - pct;
    const tone = unlimited ? "good" : toneForPct(remainingPct);
    return (
        <div data-testid={testId}>
            <div className="flex items-center justify-between text-xs text-[var(--color-text-secondary)] mb-1">
                <span>{label}</span>
                <span className="tabular-nums">
                    {unlimited ? "∞" : `${used} / ${limit}`}
                </span>
            </div>
            <div
                className="h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: COLOR.muted }}
            >
                <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                        width: `${unlimited ? 100 : pct}%`,
                        backgroundColor: COLOR[tone],
                    }}
                />
            </div>
        </div>
    );
}

export default function DashboardPlanWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/plans/current");
                setData(res);
            } catch { /* non-fatal */ } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading || !data) {
        return (
            <div
                className="rounded-2xl border p-5"
                style={{
                    backgroundColor: "var(--color-card)",
                    borderColor: "var(--color-border)",
                }}
                data-testid="dashboard-plan-widget"
            >
                <div className="h-28 animate-pulse rounded bg-white/5" />
            </div>
        );
    }

    const plan = data.plan || {};
    const usage = data.usage || {};
    const creditsPct = usage.credits_remaining_pct ?? 0;
    const creditsTone = usage.monthly_credits === -1 ? "good" : toneForPct(creditsPct);

    return (
        <div
            className="rounded-2xl border p-5"
            style={{
                backgroundColor: "var(--color-card)",
                borderColor: "var(--color-border)",
            }}
            data-testid="dashboard-plan-widget"
        >
            <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[var(--color-text-secondary)]">
                        Current plan
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                        <span className="text-2xl" aria-hidden="true">{plan.icon || "·"}</span>
                        <span
                            className="text-xl font-semibold"
                            data-testid="dashboard-current-plan-name"
                        >
                            {plan.name || plan.id}
                        </span>
                        <span
                            className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ml-1"
                            style={{
                                backgroundColor: "rgba(255, 255, 255, 0.08)",
                                color: "#FAFAFA",
                            }}
                        >
                            {plan.id}
                        </span>
                    </div>
                </div>
                {plan.id !== "enterprise" && (
                    <Link
                        to="/app/subscription"
                        data-testid="dashboard-upgrade-link"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 transition"
                        style={{
                            backgroundColor: "var(--color-primary)",
                            color: "white",
                        }}
                    >
                        Upgrade <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                )}
            </div>

            {/* Credits big */}
            <div className="rounded-xl border p-3 mb-3" style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-bg)" }}>
                <div className="flex items-end justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4" style={{ color: COLOR[creditsTone] }} />
                        <span className="text-xs uppercase tracking-widest text-[var(--color-text-secondary)]">
                            Credits balance
                        </span>
                    </div>
                    <span
                        className="text-2xl font-bold tabular-nums"
                        style={{ color: COLOR[creditsTone] }}
                        data-testid="dashboard-credits-balance"
                    >
                        {(usage.credits_balance ?? 0).toLocaleString()}
                        <span className="text-sm text-[var(--color-text-secondary)] font-normal">
                            {" "}/ {usage.monthly_credits === -1 ? "∞" : (usage.monthly_credits || 0).toLocaleString()}
                        </span>
                    </span>
                </div>
                <div
                    className="h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: COLOR.muted }}
                >
                    <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                            width: `${Math.min(100, creditsPct)}%`,
                            backgroundColor: COLOR[creditsTone],
                        }}
                    />
                </div>
            </div>

            <div className="space-y-3 mb-4">
                <Bar
                    testId="dashboard-leads-bar"
                    used={usage.leads?.used ?? 0}
                    limit={usage.leads?.limit ?? 0}
                    label="Leads this month"
                />
                <Bar
                    testId="dashboard-campaigns-bar"
                    used={usage.campaigns?.used ?? 0}
                    limit={usage.campaigns?.limit ?? 0}
                    label="Active campaigns"
                />
            </div>

            {/* Low-credit CTA */}
            {usage.monthly_credits !== -1 && creditsPct < 20 && (
                <Link
                    to="/pricing#packs"
                    data-testid="dashboard-low-credits-cta"
                    className="block mt-2 rounded-lg border px-3 py-2 text-sm"
                    style={{
                        borderColor: "rgba(250, 250, 250,0.4)",
                        backgroundColor: "rgba(250, 250, 250,0.08)",
                        color: "#FAFAFA",
                    }}
                >
                    <TrendingUp className="w-3.5 h-3.5 inline mr-1" />
                    Low on credits — top up now
                </Link>
            )}
        </div>
    );
}
